import React, { FC, useMemo } from 'react';
import { sanitize } from 'helpers/utils/htmlSanitize';
import Typography from 'components/commercetools-ui/atoms/typography';
import Wrapper from 'components/HOC/wrapper';
import Link from 'components/commercetools-ui/atoms/link';
import Button from 'components/commercetools-ui/atoms/button';
import Image from 'frontastic/lib/image';

import { GenericSectionContent, MediaCard, Cta } from '../../types';
import { useAdditionalCss } from '../../../helpers/useAdditionalCss';
import styles from './index.module.css';

const CygnusTile: FC<{ card: MediaCard }> = ({ card }) => {
  const image = card?.desktop_image;
  const cardTitle = card?.title;
  const cardDescription = card?.description;
  const ctas = card?.ctas;

  return (
    <div className="w-full relative overflow-hidden xl:min-h-[30rem] 2xl:min-h-[48rem]">
      {image && (
        <div className="relative h-[20rem] md:h-[24rem] 2xl:h-[35rem]">
          <div className="absolute z-10 h-auto w-full bg-black opacity-20"></div>
          <Image src={image?.url} alt={image?.description ?? ''} className="object-cover w-full h-full" loading='lazy' height={500} width={500}/>
        </div>
      )}
      {cardTitle && (
        <Typography className="text-16 2xl:text-16 middleresolution:text-14 xl:text-12 font-normal middleresolution:pt-16 xl:pt-8 pt-24 2xl:pt-24 leading-6 text-[#333]">
          {cardTitle}
        </Typography>
      )}
      {cardDescription && (
        <div className={styles.cardBody} dangerouslySetInnerHTML={{ __html: sanitize(cardDescription) }} />
      )}
      <div className="flex gap-10 2xl:pt-16 xl:pt-4 middleresolution:pt-8 pt-16 flex-col md:flex-row">
        {ctas && ctas.length > 0 &&
          ctas.map((cta: Cta, index: number) => (
            <Link link={cta?.url} key={index} target={cta?.target}>
              <Button
                className={
                  index === 0
                    ? 'border-1 z-10 rounded-none border border-[#2B2C39] bg-white shadow-sm hover:!bg-[#F4F4F4] hover:!border-[#F4F4F4] px-18 py-10 md:px-32 md:py-18 w-full md:w-fit'
                    : 'border-1 z-10 rounded-none border border-[#2B2C39] bg-[#2B2C39] shadow-sm hover:!bg-[#394A54] px-18 py-10 md:px-32 md:py-18 w-full md:w-fit'
                }
              >
                <Typography
                  as="span"
                  className={`2xl:text-16 xl:text-14 text-16 ${index === 0 ? 'text-[#090909]' : 'text-white'}`}
                >
                  {cta?.label}
                </Typography>
              </Button>
            </Link>
          ))}
      </div>
    </div>
  );
};

export default function CygnusRendering({ content }: { content: GenericSectionContent }) {
  const { cards, title, description, additional_css, additional_css_class_names } = content;

  const { uniqueClass, scopedAdditionalCss } = useAdditionalCss(additional_css, 'CygnusRendering');

  const slidesElement = useMemo(
    () =>
      cards?.map((card: MediaCard, index: number) => (
        <CygnusTile key={index} card={card} />
      )),
    [cards],
  );

  return (
    <>
      {scopedAdditionalCss && <style dangerouslySetInnerHTML={{ __html: scopedAdditionalCss }} />}
      <div className={`our-product-container pt-24 pb-24 lg:pt-40 lg:pb-40 2xl:pt-60 2xl:pb-60 ${[additional_css_class_names, uniqueClass].filter(Boolean).join(' ')}`}>
        <Wrapper
          clearDefaultStyles
          className="px-24 lg:px-48 md:px-28 xl:pl-60 3xl:px-0 2xl:px-80"
        >
          {title && (
            <Typography
              className="mb-24 text-center md:text-left xl:text-32 text-32 font-medium xl:leading-[2rem] leading-[3.25rem] text-[#090909] xl:text-32 2xl:text-48"
              as="h3"
            >
              {title}
            </Typography>
          )}
          {description && (
            <div
              className="pb-30 xl:pb-20 text-center xl:text-16 md:text-18 text-20 2xl:text-20 font-normal leading-8 text-[#333] xl:w-[75%] 2xl:w-[55%] md:text-left"
              dangerouslySetInnerHTML={{ __html: sanitize(description) }}
            />
          )}

          {/* CSS-responsive layout (col on mobile, row at lg) so the tiles render once, server-side. */}
          <div className="relative flex w-full flex-col gap-32 lg:flex-row lg:gap-24">{slidesElement}</div>
        </Wrapper>
      </div>
    </>
  );
}
